import {
	LayoutDashboard,
	Mail,
	Menu,
	PanelLeftClose,
	PanelLeftOpen,
	RefreshCw,
	Settings,
	Webhook,
	type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router';

import bsgLogo from '@/assets/bsg-logo-navy-wordmark.png';
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from '@/components/ui/drawer';
import { useLocalStorage } from '@/lib/use-local-storage';

/**
 * App shell (fix/shell) — the persistent sidebar-nav rail from
 * docs/design-references/resend.md § components.sidebarNav: a full-height rail
 * on the page background (no border), a centered BSG wordmark at top, then a
 * vertical list of icon+label nav items where the ACTIVE item gets an 8px
 * (rounded-lg) surface-colored highlight behind icon+label. Routed content
 * renders to the right in a scrollable content column via <Outlet/>.
 *
 * The rail collapses to an icon-only w-14 rail (labels hidden, header wordmark
 * shrunk to fit) via a bottom toggle; the collapsed/expanded choice persists in
 * localStorage. Expanded, the rail shrinks-to-fit (w-max) its widest nav item.
 *
 * Wired as a react-router layout route in App.tsx so the rail persists across
 * navigation instead of remounting per screen.
 */

interface NavItem {
	to: string;
	label: string;
	icon: LucideIcon;
}

// One entry per route (App.tsx). `end` on "/" so it isn't active for every path.
const NAV_ITEMS: readonly NavItem[] = [
	{ to: '/', label: 'Overview', icon: LayoutDashboard },
	{ to: '/delivery-log', label: 'Delivery Log', icon: Mail },
	{ to: '/settings', label: 'Settings', icon: Settings },
	{ to: '/webhooks', label: 'Webhooks', icon: Webhook },
	{ to: '/retry-processor', label: 'Retry Processor', icon: RefreshCw },
] as const;

export function AppShell() {
	const [collapsed, setCollapsed] = useLocalStorage(
		'app-shell.sidebar-collapsed',
		false,
	);
	const [drawerOpen, setDrawerOpen] = useState(false);

	return (
		<div className="flex min-h-screen bg-background text-foreground">
			{/* Desktop persistent rail — hidden below the lg (1024px) breakpoint,
			    where the mobile top bar + drawer take over. */}
			<aside
				className={[
					'hidden shrink-0 flex-col gap-6 bg-sidebar py-5 lg:flex',
					collapsed ? 'w-14 px-2' : 'w-max px-3',
				].join(' ')}
			>
				{/* Centered BSG navy wordmark (2560x655 wide lockup). Expanded:
				    height-constrained per resend.md's `.org-logo` convention.
				    Collapsed: object-contain into the narrow rail so it letterboxes
				    to a small legible mark rather than needing a separate asset. */}
				<div className="flex h-9 items-center justify-center px-1">
					<img
						src={bsgLogo}
						alt="Bridge Specialty Group"
						className={
							collapsed
								? 'h-4 w-full object-contain'
								: 'h-5 w-auto shrink-0'
						}
					/>
				</div>

				<nav className="flex flex-col gap-1">
					{NAV_ITEMS.map(({ to, label, icon: Icon }) => (
						<NavLink
							key={to}
							to={to}
							end={to === '/'}
							viewTransition
							title={collapsed ? label : undefined}
							className={({ isActive }) =>
								[
									'flex items-center rounded-lg py-2 text-sm font-medium transition-colors',
									collapsed ? 'justify-center px-2' : 'gap-3 px-3',
									isActive
										? 'bg-sidebar-accent text-sidebar-accent-foreground'
										: 'text-fg-muted hover:bg-sidebar-accent/60 hover:text-sidebar-foreground',
								].join(' ')
							}
						>
							<Icon className="size-4 shrink-0" />
							{!collapsed && <span>{label}</span>}
						</NavLink>
					))}
				</nav>

				<button
					type="button"
					onClick={() => setCollapsed(!collapsed)}
					aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
					title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
					className={[
						'mt-auto flex items-center rounded-lg py-2 text-sm font-medium text-fg-muted transition-colors hover:bg-sidebar-accent/60 hover:text-sidebar-foreground',
						collapsed ? 'justify-center px-2' : 'gap-3 px-3',
					].join(' ')}
				>
					{collapsed ? (
						<PanelLeftOpen className="size-4 shrink-0" />
					) : (
						<PanelLeftClose className="size-4 shrink-0" />
					)}
					{!collapsed && <span>Collapse</span>}
				</button>
			</aside>

			<div className="flex h-screen flex-1 flex-col overflow-hidden">
				{/* Mobile top bar — visible only below lg. Centered wordmark with a
				    hamburger trigger pinned to the left edge, opening the nav drawer. */}
				<Drawer
					direction="left"
					open={drawerOpen}
					onOpenChange={setDrawerOpen}
				>
					<header className="relative flex h-14 shrink-0 items-center justify-center border-b border-sidebar-border bg-sidebar px-3 lg:hidden">
						<DrawerTrigger
							type="button"
							aria-label="Open navigation menu"
							className="absolute left-3 flex size-9 items-center justify-center rounded-lg text-fg-muted transition-colors hover:bg-sidebar-accent/60 hover:text-sidebar-foreground"
						>
							<Menu className="size-5 shrink-0" />
						</DrawerTrigger>
						<img
							src={bsgLogo}
							alt="Bridge Specialty Group"
							className="h-5 w-auto shrink-0"
						/>
					</header>

					<DrawerContent className="bg-sidebar text-sidebar-foreground">
						<DrawerHeader className="items-center">
							<img
								src={bsgLogo}
								alt="Bridge Specialty Group"
								className="h-5 w-auto shrink-0"
							/>
							<DrawerTitle className="sr-only">Navigation</DrawerTitle>
							<DrawerDescription className="sr-only">
								Main navigation menu
							</DrawerDescription>
						</DrawerHeader>

						<nav className="flex flex-col gap-1 px-3 pb-5">
							{NAV_ITEMS.map(({ to, label, icon: Icon }) => (
								<NavLink
									key={to}
									to={to}
									end={to === '/'}
									viewTransition
									onClick={() => setDrawerOpen(false)}
									className={({ isActive }) =>
										[
											'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
											isActive
												? 'bg-sidebar-accent text-sidebar-accent-foreground'
												: 'text-fg-muted hover:bg-sidebar-accent/60 hover:text-sidebar-foreground',
										].join(' ')
									}
								>
									<Icon className="size-4 shrink-0" />
									<span>{label}</span>
								</NavLink>
							))}
						</nav>
					</DrawerContent>
				</Drawer>

				<main className="flex-1 overflow-y-auto">
					<Outlet />
				</main>
			</div>
		</div>
	);
}
